import queryString from "query-string";

const EMPTY_VALUES = new Set([null, undefined, ""]);
const toComparableNumber = (value) => {
    const number = Number(value);
    return Number.isNaN(number) ? Date.parse(value) : number;
};

const getRuleValue = (rule, column, formatDescription) => {
    const values = {
        description: formatDescription(rule),
        lastRun: rule.last_run_at,
        lastRunFixes: rule.last_run_changed_rows,
        name: rule.name,
        status: rule.state,
    };
    return values[column];
};

const compare = (actual, expected, operator) => {
    if (operator === "is null") return EMPTY_VALUES.has(actual);
    if (operator === "is not null") return !EMPTY_VALUES.has(actual);

    const actualText = String(actual ?? "").toLocaleLowerCase();
    const expectedText = String(expected ?? "").toLocaleLowerCase();
    const expectedValues = expectedText.split("#");
    const actualNumber = toComparableNumber(actual);
    const expectedNumber = toComparableNumber(expected);

    const comparisons = {
        differs: actualText !== expectedText,
        equals: actualText === expectedText,
        greater: actualNumber > expectedNumber,
        "greater or equals": actualNumber >= expectedNumber,
        in: expectedValues.includes(actualText),
        less: actualNumber < expectedNumber,
        "less or equals": actualNumber <= expectedNumber,
        like: actualText.includes(expectedText),
        "not in": !expectedValues.includes(actualText),
        "not like": !actualText.includes(expectedText),
        "not starts with": !actualText.startsWith(expectedText),
        "starts with": actualText.startsWith(expectedText),
    };

    return comparisons[operator || "equals"] ?? actualText === expectedText;
};

const matchesFilter = (rule, filter, formatDescription) => {
    const actual = getRuleValue(rule, filter.column, formatDescription);
    if (filter.operator === "is null" || filter.operator === "is not null") {
        return compare(actual, undefined, filter.operator);
    }
    const values = filter.filters || [];
    if (filter.operator === "between" || filter.operator === "not between") {
        const [minimum, maximum] = values.map(({ value }) => toComparableNumber(value));
        const actualValue = toComparableNumber(actual);
        const matches = actualValue >= minimum && actualValue <= maximum;
        return filter.operator === "between" ? matches : !matches;
    }

    return values.some(({ value }) => compare(actual, value, filter.operator));
};

export const filterRules = (rules, filters, formatDescription) =>
    (filters || []).reduce(
        (result, filter) =>
            filter?.column
                ? result.filter((rule) => matchesFilter(rule, filter, formatDescription))
                : result,
        rules,
    );

export const sortRules = (rules, sort, formatDescription) => {
    const [{ column, order } = {}] = sort || [];
    if (!column || !order) return rules;

    return [...rules].sort((left, right) => {
        const leftValue = getRuleValue(left, column, formatDescription);
        const rightValue = getRuleValue(right, column, formatDescription);
        return String(leftValue ?? "").localeCompare(String(rightValue ?? ""), undefined, {
            numeric: true,
        }) * (order === "desc" ? -1 : 1);
    });
};

export const clearRulesGridQuery = (search) => {
    const query = queryString.parse(search || "");
    delete query.filter;
    delete query.page;
    delete query.pageSize;
    delete query.sort;
    return queryString.stringify(query);
};

export const resetRulesPage = (search) => {
    const query = queryString.parse(search || "");
    delete query.page;
    return queryString.stringify(query);
};
